import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Bookmark, ChevronLeft } from 'lucide-react';
import { useBackTarget } from '../../../hooks/useBackTarget';

const FIELD_ISSUE_LABELS = {
  contact: 'Contact details',
  business: 'Business / VAT',
  address: 'Address & location',
  hours: 'Operating hours',
  social: 'Social media',
  type: 'Venue type',
  other: 'Other details',
};

const FIELD_ISSUE_STEP = {
  contact: 1,
  business: 3,
  address: 4,
  social: 5,
  hours: 6,
  type: 6,
  other: 7,
};

function readRevisionContext() {
  try {
    const draft = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
    if (draft.applicationStatus !== 'revisions_requested') return null;
    const fieldIssues = Array.isArray(draft.revisionFieldIssues) ? draft.revisionFieldIssues : [];
    const rejectedPhotos = Array.isArray(draft.rejectedPhotos) ? draft.rejectedPhotos : [];
    const rejectedVideo = !!draft.rejectedVideoUrl;
    if (!fieldIssues.length && !rejectedPhotos.length && !rejectedVideo) return null;
    return { fieldIssues, rejectedPhotos, rejectedVideo, adminNote: draft.revisionAdminNote || '' };
  } catch {
    return null;
  }
}

function VenueRevisionBanner({ context, currentStep, onJumpToStep }) {
  if (!context) return null;
  const items = [...context.fieldIssues];
  if (context.rejectedPhotos.length > 0 || context.rejectedVideo) {
    items.push({ category: 'media', note: `${context.rejectedPhotos.length} photo${context.rejectedPhotos.length === 1 ? '' : 's'}${context.rejectedVideo ? (context.rejectedPhotos.length ? ' + video' : 'video') : ''} to replace`, _isMedia: true });
  }
  if (!items.length) return null;

  return (
    <div
      data-testid="onboarding-revision-banner"
      style={{
        margin: '0 20px 12px',
        padding: '12px 14px',
        borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(255, 186, 73, 0.14), rgba(255, 240, 220, 0.95))',
        border: '1px solid rgba(214, 137, 16, 0.25)',
        boxShadow: '0 8px 18px rgba(214, 137, 16, 0.12)',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: 'rgba(214, 137, 16, 0.14)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AlertTriangle size={16} color="#B56B11" strokeWidth={2.2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700, color: 'var(--status-warning-text)' }}>
          {items.length} item{items.length === 1 ? '' : 's'} flagged by Allure
        </p>
        {context.adminNote ? (
          <p data-testid="onboarding-revision-banner-note" style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--status-warning-text)', lineHeight: 1.45, fontStyle: 'italic' }}>“{context.adminNote}”</p>
        ) : null}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {items.map((issue, idx) => {
            const label = issue._isMedia ? `Photos / Video — ${issue.note}` : (FIELD_ISSUE_LABELS[issue.category] || issue.category || 'Detail');
            const targetStep = issue._isMedia ? 7 : (FIELD_ISSUE_STEP[issue.category] ?? 7);
            const isCurrent = targetStep === currentStep;
            return (
              <button
                key={`revision-chip-${idx}`}
                type="button"
                onClick={() => onJumpToStep(targetStep)}
                data-testid={`onboarding-revision-chip-${issue._isMedia ? 'media' : issue.category}`}
                title={issue.note || label}
                style={{
                  padding: '4px 10px',
                  borderRadius: 9999,
                  border: 'none',
                  background: isCurrent ? 'var(--text)' : 'var(--surface-2)',
                  color: isCurrent ? 'var(--app-bg)' : 'var(--text-3)',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: isCurrent ? '0 4px 10px rgba(90, 58, 10, 0.22)' : '0 1px 3px rgba(214, 137, 16, 0.18)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Step {targetStep} · {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Premium shared layout for the venue onboarding steps.
 *
 * - Slim progress bar at the top showing "Step X of Y".
 * - Compact back-button row.
 * - Optional title + subtitle that don't take up the entire screen.
 * - Sticky footer with primary `Next` action and optional `Skip` link.
 * - Optional "Save & exit" pill in the header — when `onSaveAndExit` is
 *   provided, a small bookmark-icon button surfaces that lets the
 *   venue owner persist progress to the backend and step away. The
 *   parent step is responsible for the actual save (so it can supply
 *   the correct `step` number and any extra fields).
 */
export function OnboardingShell({
  step,
  totalSteps,
  title,
  subtitle,
  children,
  onBack,
  primaryLabel = 'Continue',
  onPrimary,
  primaryTestId,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  secondaryTestId,
  onSaveAndExit,
  saveAndExitLoading = false,
  testId,
  contentMaxWidth = 380,
}) {
  const goBackFallback = useBackTarget();
  const goBack = () => (onBack ? onBack() : goBackFallback());
  const navigate = useNavigate();
  const progressPct = Math.max(0, Math.min(100, (step / totalSteps) * 100));
  const revisionContext = readRevisionContext();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="app-shell"
      data-testid={testId}
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
      }}
    >
      {/* Header: back + progress bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--nav-surface)',
          backdropFilter: 'saturate(180%) blur(16px)',
          WebkitBackdropFilter: 'saturate(180%) blur(16px)',
          padding: '18px 20px 12px',
          borderBottom: '1px solid var(--hairline)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            data-testid={`${testId || 'onboarding'}-back`}
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              border: '1px solid var(--card-border)',
              background: 'var(--surface)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: 'var(--premium-shadow-nested)',
            }}
          >
            <ChevronLeft size={20} color="var(--text)" strokeWidth={2} />
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              data-testid={`${testId || 'onboarding'}-step-label`}
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-3)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Step {step} of {totalSteps}
            </p>
            <div
              aria-hidden="true"
              style={{
                marginTop: 6,
                width: '100%',
                height: 6,
                borderRadius: 999,
                background: 'var(--accent-surface-soft)',
                overflow: 'hidden',
              }}
            >
              <motion.div
                initial={false}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  height: '100%',
                  background: 'var(--accent-purple)',
                  borderRadius: 999,
                }}
              />
            </div>
          </div>

          {onSaveAndExit ? (
            <button
              type="button"
              onClick={onSaveAndExit}
              disabled={saveAndExitLoading}
              aria-label="Save progress and continue later"
              data-testid={`${testId || 'onboarding'}-save-and-exit`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 999,
                border: '1px solid var(--card-border)',
                background: 'var(--surface)',
                color: 'var(--accent-purple)',
                fontFamily: "'Inria Serif', serif",
                fontSize: 12.5,
                fontWeight: 700,
                cursor: saveAndExitLoading ? 'progress' : 'pointer',
                flexShrink: 0,
                boxShadow: 'var(--premium-shadow-nested)',
                opacity: saveAndExitLoading ? 0.7 : 1,
                transition: 'opacity 200ms ease, box-shadow 200ms ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Bookmark size={13} strokeWidth={2.4} />
              {saveAndExitLoading ? 'Saving…' : 'Save & exit'}
            </button>
          ) : null}
        </div>
      </div>

      {/* Revision banner (only when application is in revisions_requested state) */}
      <VenueRevisionBanner
        context={revisionContext}
        currentStep={step}
        onJumpToStep={(targetStep) => navigate(`/venue/onboarding/${targetStep}`)}
      />

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '100%', maxWidth: contentMaxWidth, textAlign: 'left' }}>
          {title ? (
            <h1
              data-testid={`${testId || 'onboarding'}-title`}
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontSize: 26,
                lineHeight: 1.18,
                fontWeight: 700,
                color: 'var(--text)',
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </h1>
          ) : null}
          {subtitle ? (
            <p
              data-testid={`${testId || 'onboarding'}-subtitle`}
              style={{
                margin: '8px 0 0',
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                lineHeight: 1.5,
                color: 'var(--text-3)',
                fontWeight: 500,
              }}
            >
              {subtitle}
            </p>
          ) : null}

          <div style={{ marginTop: title || subtitle ? 24 : 0 }}>{children}</div>
        </div>
      </div>

      {/* Sticky footer with primary action */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, var(--app-bg) 38%)',
          padding: '14px 20px 22px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <motion.button
          type="button"
          whileTap={{ scale: 0.97 }}
          onClick={onPrimary}
          disabled={primaryDisabled || primaryLoading}
          data-testid={primaryTestId}
          style={{
            width: '100%',
            maxWidth: contentMaxWidth,
            padding: '15px 20px',
            borderRadius: 16,
            border: 'none',
            background: primaryDisabled
              ? 'var(--accent-surface)'
              : 'var(--accent-purple)',
            color: primaryDisabled ? 'var(--text-3)' : '#FFFFFF',
            fontFamily: "'Inria Serif', serif",
            fontSize: 16,
            fontWeight: 700,
            cursor: primaryDisabled || primaryLoading ? 'default' : 'pointer',
            boxShadow: primaryDisabled
              ? 'none'
              : 'var(--premium-shadow-main)',
            transition: 'background 200ms ease, box-shadow 200ms ease',
          }}
        >
          {primaryLoading ? 'Saving…' : primaryLabel}
        </motion.button>

        {secondaryLabel ? (
          <button
            type="button"
            onClick={onSecondary}
            data-testid={secondaryTestId}
            style={{
              background: 'none',
              border: 'none',
              padding: '6px 10px',
              cursor: 'pointer',
              fontFamily: "'Inria Serif', serif",
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--text-3)',
              letterSpacing: '0.01em',
            }}
          >
            {secondaryLabel}
          </button>
        ) : null}
      </div>
    </motion.div>
  );
}
